{
  "blocks": [],
  "relationships": [],
  "localeVariants": [
    {
      "id": "new-article-transition-art-to-pregnancy",
      "locale": "fr",
      "variant": {
        "title": "De la PMA à la grossesse : la suite",
        "subtitle": "Les tests de confirmation, le passage aux soins obstétricaux, et un moment pour respirer.",
        "body": {
          "introduction": "Le passage de la PMA à une grossesse confirmée est une étape importante. Après un parcours intense de procédures et d'attente, un résultat positif peut être à la fois euphorisant et déstabilisant. Voici un aperçu calme des prochaines étapes.",
          "quickSummary": [
            {
              "title": "Les tests de confirmation d'abord.",
              "description": "La prise de sang bêta hCG et une échographie précoce (autour de 6 à 8 semaines) confirment la grossesse et vérifient l'activité cardiaque."
            },
            {
              "title": "Le suivi passe à un·e obstétricien·ne.",
              "description": "Une fois la grossesse stable, votre équipe de PMA transmet le relais à un·e obstétricien·ne ou une sage-femme."
            },
            {
              "title": "Certains traitements PMA continuent un moment.",
              "description": "Le soutien en progestérone ou œstrogènes est généralement diminué progressivement sous supervision médicale, pas arrêté d'un coup."
            }
          ],
          "sections": [
            {
              "title": "Le suivi de la grossesse précoce",
              "subtitle": "Ce que la clinique vérifie les premières semaines",
              "content": "Même après un test de grossesse positif à la maison, votre clinique de PMA confirmera généralement avec une prise de sang (bêta hCG). Plusieurs prélèvements sont souvent nécessaires pour vérifier que les niveaux hormonaux augmentent comme attendu. Une échographie précoce est habituellement programmée entre 6 et 8 semaines pour rechercher un rythme cardiaque et confirmer le nombre d'embryons implantés.",
              "bulletPoints": [
                "La prise de sang bêta hCG confirme la grossesse et suit sa progression.",
                "Des prélèvements en série (tous les quelques jours) sont la norme les premières semaines.",
                "Une échographie précoce autour de 6 à 8 semaines cherche un rythme cardiaque et compte les embryons."
              ]
            },
            {
              "title": "Le passage aux soins obstétricaux",
              "subtitle": "De l'équipe de PMA à l'obstétricien·ne",
              "content": "Une fois la grossesse stable (généralement après avoir détecté un rythme cardiaque), votre suivi passe de la spécialiste de fertilité à un·e obstétricien·ne ou une sage-femme. Cette transition peut être douce-amère, surtout si vous avez tissé des liens avec votre équipe de PMA. Demandez à votre clinique de transmettre tous les dossiers pertinents pour que votre nouveau·elle praticien·ne ait le tableau complet.",
              "bulletPoints": [
                "La transmission a généralement lieu après 8 à 10 semaines.",
                "Demandez le transfert complet du dossier, protocole et résultats de laboratoire compris.",
                "C'est normal de ressentir un mélange de soulagement et d'un petit deuil en quittant l'équipe de PMA."
              ]
            },
            {
              "title": "Ajuster les traitements",
              "subtitle": "Ne jamais arrêter de votre propre chef",
              "content": "Certains médicaments utilisés pendant la PMA (progestérone, œstrogènes) sont poursuivis en début de grossesse. Votre médecin vous guidera sur le moment et la façon de diminuer en toute sécurité. N'arrêtez aucun traitement seule, même si vous vous sentez bien : le timing compte.",
              "bulletPoints": [
                "Le soutien en progestérone se poursuit souvent jusqu'à 10 à 12 semaines.",
                "La diminution suit le calendrier de la clinique, pas le vôtre.",
                "Appelez la clinique avant d'arrêter quoi que ce soit, même si vous vous sentez très bien."
              ]
            },
            {
              "title": "Les changements physiques et les symptômes",
              "subtitle": "À quoi s'attendre les premières semaines",
              "content": "Le début de grossesse s'accompagne de fatigue, nausées, seins sensibles et fluctuations d'humeur. L'intensité varie beaucoup d'une personne à l'autre. Votre corps a beaucoup traversé pendant la PMA, et il va continuer à changer au fur et à mesure que la grossesse progresse. Les visites prénatales régulières surveilleront votre santé et celle du bébé.",
              "bulletPoints": [
                "La fatigue et les nausées légères sont courantes et rarement inquiétantes.",
                "Saignements, douleur sévère ou évanouissement : appelez immédiatement.",
                "Chaque grossesse précoce est différente : la comparaison n'aide pas."
              ]
            },
            {
              "title": "Le bien-être émotionnel",
              "subtitle": "C'est normal de ressentir plein de choses à la fois",
              "content": "Les émotions mêlées sont la norme après un résultat positif de PMA : soulagement, joie, anxiété, incrédulité. La PMA est un long parcours, et la transition vers la grossesse porte sa propre charge émotionnelle. Certaines personnes trouvent utile de poursuivre une thérapie ou un groupe de soutien.",
              "bulletPoints": [
                "C'est OK d'avoir peur même quand la nouvelle est bonne.",
                "Les groupes de soutien prénatal peuvent faire le pont entre la communauté PMA et celle de la grossesse.",
                "La thérapie qui vous a aidée pendant la PMA aide souvent en début de grossesse."
              ]
            },
            {
              "title": "Préparer le prochain chapitre",
              "subtitle": "Des petits pas, sans se presser",
              "content": "Rien n'a besoin d'être décidé aujourd'hui. Les vitamines prénatales, les rendez-vous réguliers, et éventuellement réfléchir au lieu d'accouchement font partie des mois à venir. Prenez le temps qu'il vous faut.",
              "bulletPoints": [
                "Les vitamines prénatales avec acide folique sont recommandées tôt.",
                "Le choix du lieu d'accouchement peut attendre : la plupart des personnes décident vers 20 semaines.",
                "Les cours de préparation à la naissance et les visites de maternité viendront plus tard, pas maintenant."
              ]
            }
          ]
        }
      }
    },
    {
      "id": "new-article-dealing-with-art-failure",
      "locale": "fr",
      "variant": {
        "title": "PMA sans succès : comprendre, ressentir, avancer",
        "subtitle": "Comprendre ce qui s'est passé, prendre soin de soi, et réfléchir aux prochaines étapes.",
        "body": {
          "introduction": "L'échec d'une procédure de PMA peut déclencher des réponses émotionnelles profondes. Les traitements viennent avec de grands espoirs, et la fin, quelle que soit sa forme, est vraiment difficile. Cet article aborde à la fois la dimension émotionnelle et médicale de la traversée.",
          "quickSummary": [
            {
              "title": "Le deuil est normal.",
              "description": "Faire le deuil de ce qui était espéré fait partie du processus. Donnez-vous le temps de le ressentir."
            },
            {
              "title": "Ce n'est pas votre faute.",
              "description": "Les résultats de la PMA dépendent de nombreux facteurs hors de votre contrôle. Le sentiment de culpabilité est un réflexe naturel mais pas une évaluation juste."
            },
            {
              "title": "Le soutien compte.",
              "description": "L'accompagnement, les groupes de soutien, et les échanges honnêtes avec votre équipe soignante aident tous."
            }
          ],
          "sections": [
            {
              "title": "Comprendre ce qui s'est passé",
              "subtitle": "La réalité des résultats de PMA",
              "content": "Les taux de succès en PMA varient beaucoup selon la biologie, l'âge, les conditions médicales, la génétique, et des facteurs qu'on ne comprend pas entièrement. En FIV, les facteurs d'un cycle négatif incluent la réserve ovarienne, la qualité du développement embryonnaire et la réceptivité endométriale. En IIU, la qualité du sperme et le timing jouent un rôle majeur. Un cycle négatif ne pointe pas toujours vers une cause unique.",
              "bulletPoints": [
                "Les taux de succès baissent avec l'âge mais les résultats individuels varient beaucoup.",
                "Parfois, il n'y a pas de raison unique identifiable.",
                "Revoir le cycle avec l'équipe soignante peut faire émerger des pistes pour la suite."
              ]
            },
            {
              "title": "Deuil et gestion émotionnelle",
              "subtitle": "Ce qui aide généralement",
              "content": "Les émotions peuvent aller de la tristesse et du deuil à un sentiment d'inadéquation ou de culpabilité. Ce sont des réactions naturelles, pas des signes de faiblesse. Ce qui aide varie d'une personne à l'autre, mais certaines stratégies reviennent souvent.",
              "bulletPoints": [
                "Faites votre deuil à votre rythme. Il n'y a pas à se presser.",
                "Évitez de vous blâmer : les résultats de PMA ne sont pas une mesure de votre valeur.",
                "Envisagez un accompagnement avec quelqu'un qui comprend le deuil lié à la fertilité.",
                "L'accompagnement en couple peut aider à traverser la perte ensemble.",
                "Les groupes de soutien par les pairs (en présentiel ou en ligne) réduisent l'isolement."
              ]
            },
            {
              "title": "Vivre au-delà du traitement",
              "subtitle": "Se reconnecter au reste de sa vie",
              "content": "Le parcours PMA peut tout absorber. Se reconnecter à ses loisirs, à l'activité physique et aux liens sociaux compte, non pas pour se distraire du deuil, mais parce que vous méritez des moments qui ne soient pas définis par le traitement.",
              "bulletPoints": [
                "De petites pauses loin des activités liées à la fertilité sont réparatrices.",
                "Le mouvement (marche, yoga doux) soutient la récupération émotionnelle.",
                "C'est OK de faire une vraie pause de traitement pour réévaluer."
              ]
            },
            {
              "title": "Revoir la suite avec votre équipe soignante",
              "subtitle": "Quoi demander, et quand",
              "content": "Après un cycle de PMA sans succès, un rendez-vous de suivi avec votre équipe fertilité est habituel. C'est l'occasion de revoir les données du cycle (traitements, réponse, embryologie si applicable) et de discuter d'ajustements de protocole ou d'examens complémentaires. Rien ne presse : prenez d'abord le temps qu'il vous faut.",
              "bulletPoints": [
                "Les rendez-vous de suivi ont généralement lieu 4 à 8 semaines après un cycle négatif.",
                "Demandez ce qui pourrait être ajusté dans le protocole, ce qui pourrait être testé en plus.",
                "Prendre un deuxième avis est une option raisonnable, pas une trahison de votre équipe.",
                "La planification financière compte : les cycles supplémentaires ont un vrai coût."
              ]
            },
            {
              "title": "Explorer d'autres chemins",
              "subtitle": "Quand le chemin actuel n'est pas le seul",
              "content": "Selon votre situation, d'autres options peuvent émerger lors du rendez-vous de suivi : changer de méthode PMA, envisager un don d'ovocytes ou de sperme, ou explorer l'adoption ou l'accueil familial. Ce ne sont pas toujours les étapes immédiates, mais ça vaut la peine de les connaître.",
              "bulletPoints": [
                "Changer de modalité (FIV → TEC avec un embryon congelé, IIU → FIV).",
                "Recourir à un don quand la biologie le suggère.",
                "L'adoption ou l'accueil familial comme chemins parallèles vers la parentalité.",
                "Choisir d'arrêter le traitement est aussi une décision valide."
              ]
            },
            {
              "title": "Points d'ancrage",
              "subtitle": "Ce qui reste",
              "content": "Le chemin à travers une PMA sans succès n'est pas linéaire. Le deuil, la résilience, les questions et l'espoir peuvent coexister. Le soutien communautaire (famille, amis, accompagnement, groupes) rend le processus moins isolant. La résilience, ce n'est pas l'absence de deuil : c'est la capacité à continuer d'avancer, à son propre rythme."
            }
          ]
        }
      }
    },
    {
      "id": "new-article-emotional-impact-infertility",
      "locale": "fr",
      "variant": {
        "title": "Le poids émotionnel de l'infertilité : vous n'êtes pas seule",
        "subtitle": "Ce que vous pouvez ressentir est réel, valide, et partagé par beaucoup.",
        "body": {
          "introduction": "L'infertilité n'est pas qu'un problème médical. Elle touche l'humeur, les relations, l'image de soi, l'identité, et les projets de vie. Si vous vous sentez submergée, engourdie, en colère ou vous ne vous reconnaissez plus, vous n'êtes pas seule. La recherche montre systématiquement que les personnes confrontées à l'infertilité vivent des symptômes d'anxiété et de dépression à des taux comparables à ceux de personnes atteintes d'autres maladies graves.",
          "quickSummary": [
            {
              "title": "Ce n'est pas de la sensibilité excessive.",
              "description": "Le poids émotionnel de l'infertilité est réel, mesuré, et comparable à celui d'autres maladies graves."
            },
            {
              "title": "Des ressentis communs, largement partagés.",
              "description": "Deuil, anxiété, honte, jalousie, tension dans le couple : beaucoup traversent les mêmes choses."
            },
            {
              "title": "Le soutien fait vraiment une différence.",
              "description": "Mettre des mots sur ses ressentis, protéger son énergie, trouver des personnes qui comprennent, et envisager un accompagnement professionnel : tout ça compte."
            }
          ],
          "sections": [
            {
              "title": "Pourquoi l'infertilité frappe si fort",
              "subtitle": "Ce qui la rend particulièrement dure",
              "content": "Plusieurs choses rendent l'infertilité particulièrement difficile émotionnellement : elle touche à quelque chose de profondément personnel, elle est pleine d'incertitude et d'attente, elle peut être invisible et isolante, et le traitement lui-même est intense.",
              "bulletPoints": [
                "Elle ébranle l'identité : la capacité à avoir des enfants est souvent liée à une histoire de vie imaginée depuis l'enfance.",
                "Les cycles répétés d'espoir puis de déception sont épuisants.",
                "Vu de l'extérieur, vous pouvez sembler aller bien. Beaucoup ne savent pas quoi dire, ou disent des choses maladroites.",
                "Le traitement en soi devient un travail mental à temps plein en plus de tout le reste."
              ]
            },
            {
              "title": "Des ressentis communs à beaucoup",
              "subtitle": "Vous n'êtes pas seule dans tout ça",
              "content": "L'expérience de chacune est différente, mais certains thèmes reviennent souvent. Les reconnaître peut desserrer leur emprise.",
              "bulletPoints": [
                "Deuil et perte : pas d'un bébé, mais du chemin simple qu'on avait espéré.",
                "Anxiété et bruit mental constant : Et si ça ne marche jamais ? Ai-je raté le bon moment ?",
                "Honte et culpabilité : des pensées comme « mon corps me trahit » qu'on n'appliquerait jamais à quelqu'un d'autre.",
                "Colère et jalousie : la piqûre de douleur en apprenant une grossesse n'est pas un défaut de caractère.",
                "Tensions dans le couple : les partenaires gèrent souvent différemment, et l'intimité peut devenir une tâche médicale."
              ]
            },
            {
              "title": "Ce n'est pas juste dans votre tête",
              "subtitle": "Stress et fertilité, la vraie version",
              "content": "On craint parfois qu'admettre à quel point on va mal déclenche des conseils inutiles comme « détends-toi » ou l'insinuation que c'est le stress qui aurait causé l'infertilité. Les preuves sont plus nuancées : le stress et l'humeur ne causent pas la plupart des problèmes structurels ou hormonaux. Mais un stress chronique élevé rend tout plus dur : dormir, manger, rester connectée à son ou sa partenaire. La santé mentale n'est pas un luxe ici, c'est une partie de votre santé globale dans une situation médicale difficile."
            },
            {
              "title": "Des petits pas pour tenir",
              "subtitle": "Pas besoin de tout régler d'un coup",
              "content": "Pensez en petits appuis faisables. Aucun n'est une solution miracle. Tous aident.",
              "bulletPoints": [
                "Mettez des mots sur ce que vous ressentez : même une phrase dans un journal aide.",
                "Protégez votre énergie mentale : mettez en sourdine les contenus liés à la grossesse, dites non aux événements trop lourds.",
                "Trouvez des gens qui comprennent : groupes de soutien, communautés en ligne, une amie de confiance.",
                "Envisagez un accompagnement professionnel : un·e thérapeute qui comprend la fertilité et le trauma médical."
              ]
            },
            {
              "title": "Quand demander de l'aide en urgence",
              "subtitle": "Des signes qui appellent plus qu'une conversation amicale",
              "content": "Contactez les urgences, une ligne d'écoute ou votre médecin rapidement si vous remarquez l'un des signes suivants. Ce ne sont pas des signes de faiblesse : ça veut dire que votre système nerveux est submergé et a besoin de plus de soutien.",
              "bulletPoints": [
                "Des pensées persistantes du type « tout le monde serait mieux sans moi ».",
                "Des idées ou comportements d'automutilation.",
                "L'incapacité à sortir du lit, à travailler, ou à gérer les tâches de base.",
                "Des crises de panique qui ne s'apaisent pas, ou une anxiété écrasante au quotidien.",
                "Un recours croissant à l'alcool ou à d'autres substances pour tenir."
              ]
            },
            {
              "title": "Vous n'êtes pas seule dans tout ça",
              "subtitle": "Des points d'ancrage à garder",
              "content": "L'infertilité peut réduire le monde aux salles d'attente, aux prises de sang et aux calendriers. Même quand la vie de tout le monde semble avancer et la vôtre bloquée, vous n'êtes pas en échec et vous n'êtes pas seule. Ce que vous ressentez est réel, valide, et partagé par beaucoup d'autres qui traversent un chemin similaire. Avoir besoin d'aide ne veut pas dire que vous êtes faible. Ça veut dire que vous avez été forte trop longtemps sans assez de soutien."
            }
          ]
        }
      }
    },
    {
      "id": "new-article-stim-daily-tips",
      "locale": "fr",
      "variant": {
        "title": "Stimulation ovarienne : petits gestes qui aident",
        "subtitle": "Timing, injections, monitoring, ballonnements : les repères concrets pour la stim.",
        "body": {
          "introduction": "La stimulation ovarienne est la phase de la FIV (ou de la congélation d'ovocytes) où l'on prend des traitements pendant environ 8 à 13 jours pour faire grandir plusieurs follicules en même temps. Les visites de monitoring vérifient comment le corps répond et la clinique ajuste le plan en conséquence. C'est intense : physique, émotionnel, et minuté à la fois. Voici quelques petites routines pour tenir le rythme.",
          "quickSummary": [
            {
              "title": "Le timing, c'est tout.",
              "description": "La stimulation est un de ces rares moments où arriver 30 minutes en retard peut vraiment compter. Choisissez une heure, mettez deux alarmes, protégez-la."
            },
            {
              "title": "Les symptômes sont des infos, pas des prédictions.",
              "description": "Ballonnements, sautes d'humeur, fatigue : tout ça reflète les hormones et la croissance ovarienne. Ça ne dit rien sur le résultat du cycle."
            },
            {
              "title": "Sachez quand appeler la clinique le jour même.",
              "description": "Douleur sévère, gonflement abdominal rapide, essoufflement : ces signes méritent un appel dans la journée."
            }
          ],
          "sections": [
            {
              "title": "Faites du timing votre priorité",
              "subtitle": "Les injections n'attendent pas",
              "content": "Les traitements de stimulation ont des fenêtres étroites. Choisissez une heure quotidienne que vous pouvez réellement protéger, en semaine comme en week-end. Mettez deux alarmes : une 15 minutes avant, une à l'heure exacte. Si vous voyagez ou changez de fuseau, prévenez la clinique avant pour ne pas avoir à deviner. Si vous ratez ou retardez une dose, ne paniquez pas et ne doublez pas de vous-même. Appelez la clinique et suivez leurs instructions.",
              "bulletPoints": [
                "Choisissez une heure que vous pouvez protéger tous les jours.",
                "Deux alarmes : une 15 min avant, une à l'heure exacte.",
                "Voyage entre fuseaux : prévenez la clinique d'abord.",
                "Dose ratée : appelez, ne devinez pas, ne doublez pas."
              ]
            },
            {
              "title": "Créez une routine d'injection de 5 minutes",
              "subtitle": "La fatigue décisionnelle est le vrai ennemi",
              "content": "Le problème n'est généralement pas l'injection en soi, mais les décisions qui l'entourent. Un setup calme aide : un endroit dédié à la maison, tout le matériel dans une seule boîte, et une petite checklist que vous suivez de la même manière chaque fois. Les deux premiers jours sont souvent les plus durs. Après, ça devient une routine."
            },
            {
              "title": "Planifiez vos matinées comme si le monitoring pouvait tomber",
              "subtitle": "Parce qu'il tombera souvent",
              "content": "Les visites de monitoring peuvent bousculer une journée de travail rapidement, surtout quand la clinique ajuste le planning selon les résultats. Gardez vos matinées flexibles si possible : les réunions tôt sont les plus difficiles à protéger pendant la stim. Enregistrez le parking et le contact de la clinique dans vos favoris. Prenez un en-cas si votre clinique combine prise de sang et échographie ; vous pouvez y rester plus longtemps que prévu."
            },
            {
              "title": "Attendez-vous à des ballonnements et à une pesanteur pelvienne",
              "subtitle": "Adaptez votre quotidien",
              "content": "Au fur et à mesure que les follicules grandissent, les ovaires prennent du volume. Se sentir gonflée, ballonnée ou crampée est complètement normal, surtout les derniers jours avant la ponction. Les pantalons amples et sous-vêtements respirants aident. De petits repas plus fréquents sont plus faciles que de gros. Prévoyez des soirées plus calmes vers la fin de la stimulation : c'est là que la pesanteur culmine généralement.",
              "bulletPoints": [
                "Les vêtements amples aident plus qu'on ne le croit.",
                "De petits repas, plus souvent, si vous êtes inconfortable.",
                "La constipation est fréquente. Demandez à la clinique avant d'auto-médicamenter.",
                "Des soirées plus calmes les derniers jours aident à tenir."
              ]
            },
            {
              "title": "Gardez l'activité physique douce",
              "subtitle": "Faible impact dès que les ovaires prennent du volume",
              "content": "La plupart des cliniques recommandent d'éviter les sports à fort impact, le port de charges lourdes et les torsions dès que les ovaires sont enlargis : l'inconfort et le risque (rare) de torsion ovarienne augmentent. Un mouvement doux reste généralement OK. Marche, yoga doux sans torsions profondes, étirements légers font de bons choix par défaut."
            },
            {
              "title": "L'hydratation est un vrai outil de confort",
              "subtitle": "Pas un chiffre magique, juste de la régularité",
              "content": "Il n'y a pas de dose parfaite. Mais la déshydratation rend les ballonnements, maux de tête et fatigue plus difficiles à supporter. Commencez la journée avec de l'eau. Ajoutez un verre après les visites de monitoring. Si vous vous sentez gonflée ou crampée, une hydratation régulière aide plus que de tout boire d'un coup. Si votre clinique mentionne un risque d'HSO, suivez leurs consignes spécifiques."
            },
            {
              "title": "Les symptômes ne sont pas un tableau de scores",
              "subtitle": "Se sentir bien ou se sentir mal, les deux sont normaux",
              "content": "Pendant la stimulation, les symptômes reflètent les hormones et la croissance ovarienne, pas la réussite du cycle. Ballonnements, seins sensibles, sautes d'humeur et fatigue sont tous fréquents. Certaines personnes se sentent bien, d'autres ont du mal. Les deux sont normaux. Les symptômes sont une info sur votre corps, pas une prédiction du résultat."
            },
            {
              "title": "Protégez votre tête, pas juste votre corps",
              "subtitle": "De petites limites aident beaucoup",
              "content": "La stimulation peut prendre toute votre énergie mentale. Choisissez une source d'info de confiance (le portail de la clinique, cette app, ou un seul site d'éducation patient). Limitez le scroll de forums quand vous remarquez que ça augmente l'anxiété. Dites à une ou deux personnes exactement comment vous soutenir : transport, repas, distraction, check-ins."
            },
            {
              "title": "Sachez ce qui mérite un appel le jour même",
              "subtitle": "La plupart des effets sont gérables, mais ceux-ci ne le sont pas",
              "content": "La plupart des symptômes de la stimulation sont gérables, mais votre clinique veut être informée rapidement si l'un des signes suivants apparaît :",
              "bulletPoints": [
                "Douleur abdominale sévère ou qui s'aggrave.",
                "Prise de poids rapide ou augmentation rapide du tour de taille.",
                "Essoufflement.",
                "Vomissements persistants ou incapacité à garder les liquides.",
                "Sensation de malaise ou d'être inhabituellement mal."
              ]
            },
            {
              "title": "Le déclic mental qui aide",
              "subtitle": "La stim, c'est un sprint, pas un marathon",
              "content": "Essayez de traiter la stimulation comme un sprint court et limité dans le temps. Vous n'êtes pas censée tout faire parfaitement. Présentez-vous, respectez le timing, signalez les symptômes honnêtement, et laissez la clinique ajuster. Vous en faites assez."
            }
          ]
        }
      }
    }
  ]
}
